import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./Notifications.module.css";
import { usePermission } from "../../hooks/usePermissions";

const AutoNotifications = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queries, setQueries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    notification_query_id: ""
  });

  const { hasPermission } = usePermission();

  const fetchAutoAlerts = async () => {
    setLoading(true);
    try {
      const response = await api.get("/auto-stock-alerts");
      setAlerts(response.data || []);
    } catch (error) {
      console.error("Error fetching auto stock alerts:", error);
      toast.error("Failed to load auto notifications");
    } finally {
      setLoading(false);
    }
  };

  const fetchQueries = async () => {
    try {
      const response = await api.get("/notification-query");
      if (response.data && response.data.status) {
        setQueries(response.data.data || []);
      } else if (Array.isArray(response.data)) {
        setQueries(response.data);
      }
    } catch (error) {
      console.error("Error fetching notification queries:", error);
    }
  };

  useEffect(() => {
    fetchAutoAlerts();
    fetchQueries();
  }, []);

  const handleToggle = async (id, currentStatus) => {
    try {
      // Optimistic update
      setAlerts((prev) =>
        prev.map((alert) =>
          alert.id === id ? { ...alert, is_active: !currentStatus } : alert
        )
      );

      const response = await api.patch(`/auto-stock-alerts/${id}/toggle`);
      if (response.status) {
        toast.success("Condition status updated successfully");
      }
    } catch (error) {
      console.error("Error toggling alert:", error);
      toast.error("Failed to update status");
      // Revert optimistic update on failure
      fetchAutoAlerts();
    }
  };

  const handleEditClick = (rule) => {
    setEditingId(rule.id);
    setFormData({
      name: rule.name,
      description: rule.description || "",
      notification_query_id: rule.notification_query_id || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) return;
    
    try {
      const response = await api.delete(`/auto-stock-alerts/${id}`);
      // The interceptor strips the wrapper, so we just check response.status
      if (response.status) {
        toast.success("Rule deleted successfully");
        fetchAutoAlerts();
      } else {
        toast.error(response.message || "Failed to delete rule");
      }
    } catch (error) {
      console.error("Error deleting rule:", error);
      const errMsg = error?.response?.data?.message || error?.message || "Failed to delete rule";
      toast.error(errMsg);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.notification_query_id) {
      toast.error("Name and Target Audience are required");
      return;
    }
    
    try {
      if (editingId) {
        const response = await api.put(`/auto-stock-alerts/${editingId}`, formData);
        if (response.status) {
          toast.success(response.message || "Rule updated successfully");
        }
      } else {
        const response = await api.post("/auto-stock-alerts", formData);
        if (response.status) {
          toast.success(response.message || "Rule created successfully");
        }
      }
      setShowModal(false);
      setEditingId(null);
      setFormData({ name: "", description: "", notification_query_id: "" });
      fetchAutoAlerts(); // Refresh list
    } catch (error) {
      console.error("Error saving alert rule:", error);
      // Make sure we extract the actual error message even if it's deeply nested
      const errMsg = error?.response?.data?.message || error?.message || "Failed to save rule";
      toast.error(errMsg);
    }
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAlerts = alerts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(alerts.length / itemsPerPage);

  return (
    <div className={styles.container}>
      <ToastContainer position="bottom-right" autoClose={2000} />

      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>Auto Notifications</h1>
          <p className={styles.pageSubtitle}>Manage automated gap up/down stock alerts</p>
        </div>
        {hasPermission("MODIFY_NOTIFICATIONS") && (
          <button className={styles.addBtn} onClick={() => {
            setEditingId(null);
            setFormData({ name: "", description: "", notification_query_id: "" });
            setShowModal(true);
          }}>
            + Create Rule
          </button>
        )}
      </div>

      <div className={styles.fullWidthContainer}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.w60}>ID</th>
              <th>Condition Name</th>
              <th>Description</th>
              <th>Target Audience</th>
              <th className={styles.w160}>Status</th>
              <th className={styles.w160}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && alerts.length === 0 ? (
              <tr>
                <td colSpan={6} className={styles.emptyState}>
                  Loading auto notifications...
                </td>
              </tr>
            ) : currentAlerts.length > 0 ? (
              currentAlerts.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td className={styles.fontMedium}>{row.name}</td>
                  <td className={styles.maxW250}>{row.description}</td>
                  <td>{row.query_title || "Unknown"}</td>
                  <td>
                    <label className={`${styles.toggleSwitch} ${styles.toggleLabel}`}>
                      <input
                        type="checkbox"
                        checked={row.is_active}
                        onChange={() => handleToggle(row.id, row.is_active)}
                        disabled={!hasPermission("MODIFY_NOTIFICATIONS")}
                        className={styles.toggleInput}
                      />
                      <span className={`${styles.toggleText} ${row.is_active ? styles.toggleTextActive : styles.toggleTextInactive}`}>
                        {row.is_active ? "Active" : "Inactive"}
                      </span>
                    </label>
                  </td>
                  <td>
                    <div className={styles.actionsCell}>
                      <button 
                        className={`${styles.iconBtn} ${styles.edit}`}
                        onClick={() => handleEditClick(row)}
                        disabled={!hasPermission("MODIFY_NOTIFICATIONS")}
                        title="Edit Rule"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" /></svg>
                      </button>
                      <button 
                        className={`${styles.iconBtn} ${styles.delete}`}
                        onClick={() => handleDelete(row.id)}
                        disabled={!hasPermission("MODIFY_NOTIFICATIONS")}
                        title="Delete Rule"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className={styles.emptyState}>
                  No auto notifications found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {alerts.length > itemsPerPage && (
        <div className={`${styles.pagination} ${styles.paginationContainer}`}>
          <div className={styles.paginationInfo}>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, alerts.length)} of {alerts.length} entries
          </div>
          <div className={styles.paginationControls}>
            <button 
              className={styles.pageBtn} 
              disabled={currentPage === 1} 
              onClick={() => setCurrentPage(p => p - 1)}
            >
              Prev
            </button>
            <span className={styles.paginationStatus}>
              Page {currentPage} of {totalPages}
            </span>
            <button 
              className={styles.pageBtn} 
              disabled={currentPage === totalPages} 
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
      </div>

      {showModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2 className={styles.modalHeading}>
              {editingId ? "Edit Rule" : "Create New Rule"}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Rule Name *</label>
                <input 
                  className={`${styles.select} ${styles.formInput}`}
                  type="text" 
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. Volume Spike > 500%"
                  required 
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Description</label>
                <textarea 
                  className={`${styles.select} ${styles.formTextarea}`}
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional description"
                />
              </div>
              
              <div className={styles.formGroupLarge}>
                <label className={styles.formLabel}>Target Audience *</label>
                <select 
                  className={`${styles.select} ${styles.formInput}`}
                  value={formData.notification_query_id}
                  onChange={e => setFormData({...formData, notification_query_id: e.target.value})}
                  required
                >
                  <option value="">Select Audience...</option>
                  {queries.map(q => (
                    <option key={q.id} value={q.id}>{q.query_title}</option>
                  ))}
                </select>
              </div>
              
              <div className={styles.formFooter}>
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)}
                  className={styles.cancelBtn}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={`${styles.addBtn} ${styles.submitBtn}`}
                >
                  Save Rule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoNotifications;
